import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ROUTE_DESTINATIONS } from "../../../constants/routes";
import { DestinationTypeEnum } from "../../../enums/destination";
import useSelectedAccount from "../../../hooks/useSelectedAccount";
import { AllDestinations } from "../../../models/destination";
import { useGetDestinationsQuery } from "../../../redux/services/destinations";
import { useExportArticleToGoogleDocMutation } from "../../../redux/services/google";

export type GoogleExportStatus =
  | "idle"
  | "exporting"
  | "done"
  | "error"
  | "reauth";

export const GOOGLE_EXPORT_LABELS: Record<GoogleExportStatus, string> = {
  idle: "Export to Google Docs",
  exporting: "Exporting...",
  done: "Exported",
  error: "Export failed. Click to retry",
  reauth: "Connect a Google Docs destination",
};

// what the server says when the destination has no working Google connection
const REAUTH_MESSAGE = /connect the google account again|revoked/i;

// Export one article to a Google Doc in the folder of the account's connected Google Docs destination. Shared by the article view button and the table menu.
export const useGoogleDocExport = (articleId: string) => {
  const { accountId } = useSelectedAccount();
  const navigate = useNavigate();
  const { data: destinations, isSuccess: isDestinationsLoaded } =
    useGetDestinationsQuery(
      // limit set high enough to cover every destination on the account, so a Google Docs
      // destination past the first page doesn't get missed and wrongly treated as disconnected
      { accountId, offset: 0, limit: 1000, includeTotalCount: true },
      { skip: !accountId }
    );
  const [exportToGoogleDoc] = useExportArticleToGoogleDocMutation();

  // the first Google Docs destination that is connected and not flagged for reconnect
  const destination: AllDestinations | undefined = destinations?.data?.find(
    (d: AllDestinations) =>
      d.destinationTypeId === DestinationTypeEnum.GOOGLE_DOCS &&
      "googleEmail" in d.destination &&
      d.destination.googleEmail &&
      !("needsReconnect" in d.destination && d.destination.needsReconnect)
  );

  const [status, setStatus] = useState<GoogleExportStatus>("idle");
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  // no connected Google Docs destination (or access revoked): send to the destinations page instead of exporting
  const view: GoogleExportStatus =
    status === "idle" && isDestinationsLoaded && !destination
      ? "reauth"
      : status;

  const exportArticle = async () => {
    if (!accountId || !destination?.destinationId) return;
    setStatus("exporting");
    try {
      const doc = await exportToGoogleDoc({
        accountId,
        articleId,
        destinationId: destination.destinationId,
      }).unwrap();
      setStatus("done");
      toast.success(
        <span>
          Exported to Google Docs.{" "}
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Open document
          </a>
        </span>,
        { duration: 8000 }
      );
      timer.current = setTimeout(() => setStatus("idle"), 1500);
    } catch (error: any) {
      const detail: string | undefined = error?.data?.detail;
      if (detail && REAUTH_MESSAGE.test(detail)) {
        setStatus("reauth");
      } else {
        setStatus("error");
        toast.error(detail ?? "Could not export the article.");
      }
    }
  };

  // click: go connect a destination when Google needs it, otherwise export (ignored while busy or just done)
  const run = () => {
    if (view === "exporting") return;
    if (view === "reauth") {
      navigate(ROUTE_DESTINATIONS);
    } else if (view !== "done") {
      exportArticle();
    }
  };

  return { view, run };
};
